// ==============================|| TENANT LANDING PAGE (LAND-CFG) ||============================== //
// Her SPA'nın açılış sayfası artık kiracı tarafından AYARLANABİLİR. Değer, marka `extras` jsonb
// torbasında yaşar — customCss (C-7) ve locale varsayılanlarının (LocaleDefaults) BİREBİR aynı deseni:
// şema migrasyonu YOK, yeni uç YOK, ve değer ilk boyamadan ÖNCE zaten elimizde
// (`resolveBrandingBeforePaint` → mapBranding), yani yönlendirme kurulurken hazırdır. Ek istek yoktur
// ve yanlış sayfanın bir an görünüp kaybolması (flash) olmaz.
//
// super-admin BİLEREK YOK: o SPA platform kapsamındadır, hiçbir kiracının içinde değildir —
// açılışını bir kiracının markasından sürmek yanlış olurdu. marketing de yok (herkese açık, girişi
// ve route tablosu yok).

/** Ayarlanabilir açılış sayfası olan SPA'lar. Anahtarlar `extras.landingPages` içinde camelCase. */
export const LANDING_APPS = ['admin', 'student', 'teacher'] as const;

export type LandingApp = (typeof LANDING_APPS)[number];

/**
 * `extras.landingPages` okuması — TOLERANT. Torba opaktır ve elle (ya da eski bir sürümle)
 * yazılmış olabilir; string olmayan/eksik her şey YOK sayılır. Fırlatmak, tek bozuk anahtar
 * yüzünden tüm temayı düşürmek olurdu.
 */
export function readLandingPages(extras: unknown): Partial<Record<LandingApp, string>> {
  if (!extras || typeof extras !== 'object') {
    return {};
  }

  const bag = (extras as Record<string, unknown>).landingPages;
  if (!bag || typeof bag !== 'object') {
    return {};
  }

  const source = bag as Record<string, unknown>;
  const out: Partial<Record<LandingApp, string>> = {};
  for (const app of LANDING_APPS) {
    const value = source[app];
    if (typeof value === 'string' && value.startsWith('/')) {
      out[app] = value;
    }
  }

  return out;
}

export type ResolveLandingOptions = {
  /** Kiracının ayarladığı yol (yoksa undefined). */
  configured: string | undefined;
  /** Bu SPA'nın İZİN VERDİĞİ açılış yolları — küratörlü liste (serbest metin DEĞİL). */
  allowed: readonly string[];
  /** SPA'nın kendi sabit varsayılanı — doğrulanmaz, son sığınaktır. */
  fallback: string;
  /**
   * Bu yol BU kullanıcı için açılabilir mi (rol/izin/modül)? Menü gizlemeyle aynı semantik.
   * Verilmezse erişilebilirlik sınanmaz.
   */
  isReachable?: (path: string) => boolean;
};

/**
 * Açılış yolunu çözer. FAIL-SAFE: her belirsiz durumda SPA'nın sabit varsayılanına döner.
 *
 * <para>Üç kapı, üçü de gerçek bir hatayı önler:</para>
 * 1. `allowed` kontrolü — küratörlü listede olmayan bir yol (elle yazılmış ya da route kaldırıldıktan
 *    sonra öksüz kalmış bir ayar) `*` catch-all'ı tetikler ve catch-all da açılışa döndüğü için
 *    GİRİŞTE SONSUZ YÖNLENDİRME oluşurdu.
 * 2. `isReachable` — yol var ama kullanıcının rolü/modülü yoksa açılışta 403 karşılardı. Kiracı
 *    yöneticisinin tüm öğrencileri açamadıkları bir sayfaya kilitlemesi böyle engellenir.
 * 3. Varsayılan ASLA doğrulanmaz: uygulamanın kendi sabiti güvenilir kabul edilir, yoksa çözüm
 *    hiçbir şey döndüremeyebilirdi.
 */
export function resolveLandingRoute({
  configured,
  allowed,
  fallback,
  isReachable
}: ResolveLandingOptions): string {
  if (!configured || !allowed.includes(configured)) {
    return fallback;
  }

  if (isReachable && !isReachable(configured)) {
    return fallback;
  }

  return configured;
}

/**
 * Ayarı `extras` torbasına YAZMAK için birleştirme — kaydeden ekran bunu kullanır.
 * Boş/varsayılana eşit değerler anahtarı SİLER, böylece torba ölü ayarla şişmez ve
 * "ayarlanmamış" ile "varsayılana ayarlanmış" aynı şey olarak kalır.
 */
export function mergeLandingPages(
  extras: unknown,
  next: Partial<Record<LandingApp, string | undefined>>
): Record<string, unknown> {
  const bag = extras && typeof extras === 'object' ? { ...(extras as Record<string, unknown>) } : {};
  const current = readLandingPages(extras);
  const merged: Record<string, string> = { ...current };

  for (const app of LANDING_APPS) {
    if (!(app in next)) {
      continue;
    }
    const value = next[app];
    if (value) {
      merged[app] = value;
    } else {
      delete merged[app];
    }
  }

  if (Object.keys(merged).length === 0) {
    delete bag.landingPages;
  } else {
    bag.landingPages = merged;
  }

  return bag;
}
